import { useInfiniteQuery, useQuery, useMutation } from '@tanstack/react-query';
import queryString from 'query-string';
import { httpService } from '../../../utils/apiService';

export const useFetchChargerHistory = (pagination, operatorId) => {
  return useQuery({
    queryKey: [
      'chargingHistory',
      pagination.offset,
      pagination.limit,
      pagination.status,
      pagination.searchValue, // Ensure RFID or other search values are included
      pagination.startDate,
      pagination.endDate,
      pagination.isRFID, // Include isRFID in query key
      operatorId,
    ],
    queryFn: async () => {
      // Construct query parameters
      const params = queryString.stringify({
        offset: pagination.offset,
        limit: pagination.limit,
        status: pagination.status || '',
        startDate: pagination.startDate || '',
        endDate: pagination.endDate || '',
        isRFID: pagination.isRFID, // Pass isRFID in query params
        ...(pagination.searchType === 'RFID' && pagination.searchValue
          ? { RFID: pagination.searchValue } // Pass RFID in query params
          : {}),
      });

      // Only add key & id in the request body if search type is NOT RFID
      let data = {
        operatorId,
        ...(pagination.searchType &&
        pagination.searchType !== 'RFID' &&
        pagination.searchValue
          ? { key: pagination.searchType, id: pagination.searchValue }
          : {}),
      };

      // Make API call with params and data
      const response = await httpService.post(
        `charger-history/list?${params}`, // RFID is passed as query param
        data
      );

      return response.data.data;
    },

    refetchOnWindowFocus: false,
  });
};

export const useGetPaymentInfoFromTranscation = (params = {}, options = {}) => {
  const { id } = params;

  return useQuery({
    queryKey: ['GetPaymentInfo', id],
    queryFn: async () => {
      const response = await httpService.get(
        `/charger-history/payment-info/${id}`
      );

      return response.data.data;
    },
    ...options,
  });
};

export const useGetSingleCustomer = (params = {}, options = {}) => {
  const { id } = params;

  return useQuery({
    queryKey: ['GetSingleCustomer', id],
    queryFn: async () => {
      const response = await httpService.post(
        `/customer/single-customer/${id}`
      );

      return response.data.data.singleCustomerData;
    },
    ...options,
  });
};

export const useFetchWithdrawalList = (
  { offset, limit, status, searchValue },
  token,
  clientId,
  date
) => {
  return useQuery({
    queryKey: ['withdrawalList', offset, status, searchValue, date, clientId],
    queryFn: async () => {
      const params = queryString.stringify({
        offset,
        limit,
        status,
        startDate: date.startDate.toString() || '',
        endDate: date.endDate.toString() || '',
        clientId,
      });
      // let data = {
      //   clientId,
      //   ...(searchType && searchValue ? {key: searchType, id: searchValue} : {}),
      // };

      const response = await httpService.get(
        `client-withdrawal/list-Admin?${params}`,
        token
      );

      return response.data.data;
    },

    enabled: !!token && !!clientId,
    refetchOnWindowFocus: false,
  });
};

export const useGetChargerSelect = (params = {}, options = {}) => {
  const { token, clientId, search, offset, limit } = params;

  return useInfiniteQuery({
    queryKey: ['GET_CHARGER_SELECT', search],
    queryFn: async ({ pageParam = offset }) => {
      const queryParms = queryString.stringify({
        search,
        offset: pageParam,
        limit,
      });
      const response = await httpService.post(
        `charger/get-charger-select?${queryParms}`,
        token,
        {
          clientId,
        }
      );

      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPageNumber = allPages.length + 1;
      const totalPages = Math.ceil(lastPage.totalCount / limit);

      return nextPageNumber <= totalPages ? nextPageNumber : undefined;
    },

    ...options,
  });
};

export const useGetStationSelect = (params = {}, options = {}) => {
  const { token, clientId, search, offset, limit } = params;

  return useInfiniteQuery({
    queryKey: ['GET_STATION_SELECT', search],
    queryFn: async ({ pageParam = offset }) => {
      const queryParms = queryString.stringify({
        search,
        offset: pageParam,
        limit,
      });
      const response = await httpService.post(
        `charger-station/get-station-select?${queryParms}`,
        token,
        { clientId }
      );

      return response.data.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPageNumber = allPages.length + 1;
      const totalPages = Math.ceil(lastPage.totalCount / limit);

      return nextPageNumber <= totalPages ? nextPageNumber : undefined;
    },

    ...options,
  });
};

export const useGetCustomerSelect = (params = {}, options = {}) => {
  const { operatorId, search, offset, limit } = params;

  return useInfiniteQuery({
    queryKey: ['GET_CUSTOMER_LIST', operatorId, search],
    queryFn: async ({ pageParam = offset }) => {
      const queryParms = queryString.stringify({
        search,
        offset: pageParam,
        limit,
      });
      const response = await httpService.post(
        `customer/get-customer-select?${queryParms}`,
        { operatorId }
      );

      return response.data.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPageNumber = allPages.length + 1;
      const totalPages = Math.ceil(lastPage.totalCount / limit);

      return nextPageNumber <= totalPages ? nextPageNumber : undefined;
    },

    ...options,
  });
};
export const useGetSingleTransaction = (params = {}, options = {}) => {
  const { id, operatorId } = params;

  return useQuery({
    queryKey: ['GET_SINGLE_TRANSACTION', id],
    queryFn: async () => {
      const response = await httpService.post(`/charger-history/single/${id}`, {
        operatorId,
      });

      return response.data.data;
    },
    ...options,
  });
};

export const useSingleTransactionLog = (params = {}, options = {}) => {
  const { transactionId, operatorId } = params;

  return useQuery({
    queryKey: ['singleTransactionLog', transactionId],
    queryFn: async () => {
      const response = await httpService.post(
        `/charger-history-logs/single-charging-history-log/${transactionId}`,
        { operatorId }
      );

      return response.data.data;
    },
    ...options,
  });
};

export const useSettleTransactionManually = () => {
  return useMutation({
    mutationFn: async ({ transactionId, operatorId, data }) => {
      const response = await httpService.post(
        `/charger-history/settle-manually/${transactionId}`,
        {
          operatorId,
          ...data,
        }
      );
      return response.data;
    },
  });
};

